// @flow strict

import * as React from 'react'
import {
  Button,
  AccessibilityText,
  TrackEvents,
  formatNumberOfAccounts,
  URLS,
} from '@mondough/public-web-components'

import { MarkdownContent } from './MarkdownContent'
import { SectionWrapper, SectionThemes } from './SectionWrapper'

import styles from './CTASection.module.scss'
import appStoreLogo from '../../static/images/appStoreLogo.svg'
import playStoreLogo from '../../static/images/playStoreLogo.svg'

export type CTAProps = {
  ctaType: 'appStore' | 'download',
  title: string,
  subtitle?: string,
  reverse: boolean,
}

const CTASection = ({ ctaType, title, subtitle, reverse }: CTAProps) => {
  const [numberOfAccounts, setNumberOfAccounts] = React.useState(null)

  React.useEffect(() => {
    function fetchNumberOfAccounts() {
      fetch('https://api.monzo.com/user-counter/count')
        .then(rsp => rsp.json())
        .then(data => {
          if (data.count) {
            setNumberOfAccounts(data.count)
          }
        })
        .catch(err => console.warn(err))
    }

    // Only make the request if no text is supplied, as the user number is only
    // shown in the fallback text
    if (!subtitle) {
      fetchNumberOfAccounts()
    }
  }, [subtitle])

  const fallbackText = `And join the ${
    numberOfAccounts ? formatNumberOfAccounts(numberOfAccounts) : 'millions of '
  } people who’ve already changed the way they bank.`

  return (
    <SectionWrapper
      className={styles.section}
      contentClassName={styles.container}
      theme={reverse ? SectionThemes.GREY : SectionThemes.LIGHT}
    >
      <div className={styles.columnOne} id="CTASection">
        <h2 className={styles.title}>{title}</h2>
        <MarkdownContent>{!subtitle ? fallbackText : subtitle}</MarkdownContent>
      </div>
      <div className={styles.columnTwo}>
        {ctaType === 'appStore' && (
          <div className={styles.appButtons}>
            <TrackEvents
              category="conversion"
              action="download app"
              label="apple"
            >
              <a data-testid="app-store-link" href={URLS.ADJUST_APP_STORE}>
                <AccessibilityText>
                  Download Monzo from the App Store
                </AccessibilityText>
                <img
                  src={appStoreLogo}
                  alt=""
                  className={styles.appStoreLogo}
                />
              </a>
            </TrackEvents>
            <TrackEvents
              category="conversion"
              action="download app"
              label="google"
            >
              <a
                data-testid="play-store-link"
                className={styles.playStoreButton}
                href={URLS.ADJUST_PLAY_STORE}
              >
                <AccessibilityText>
                  Download Monzo from the Google Play Store
                </AccessibilityText>
                <img
                  src={playStoreLogo}
                  alt=""
                  className={styles.appStoreLogo}
                />
              </a>
            </TrackEvents>
          </div>
        )}
        {ctaType === 'download' && (
          <TrackEvents
            category="conversion"
            action="download app"
            label="download"
          >
            <Button href={URLS.ADJUST_BOTH_STORE}>Download Monzo</Button>
          </TrackEvents>
        )}
      </div>
    </SectionWrapper>
  )
}

export default CTASection
